import { Stack, Typography } from "@mui/material";
import { orange } from "@mui/material/colors";
import { useAxesTooltip } from "@mui/x-charts-pro";
import ChartTooltip from "components/Dashboard/ChartTooltip";
import { RcraInfoFederalWasteGeneratorStatus } from "generated-graphql/graphql";
import { useMemo } from "react";
import { prettyPrintMonthYear } from "util/dates";

import { FederalGeneratorStatusIndicator } from "../../FederalGeneratorStatusIndicator";
import { colorMap } from "../utils";
import { OverThresholdDot } from "./OverThresholdDot";

type SeriesValue = { color: string; value: string };

/** The grey layer's labels as the chart's valueFormatter emits them, one per stack. */
const EPISODIC_LAYERS = ["Vendor Episodic (excluded)", "e‑Manifest Episodic (excluded)"] as const;

/**
 * One row for the excluded pounds. Both stacks' layers are the same colour and
 * almost always the same pounds, so the row names them once; when a source's own
 * cap makes them differ, the row shows each.
 */
function episodicRow(
  seriesItems: ReadonlyMap<string, SeriesValue>
): { color: string; capitalizedLabel: string; value: string } | null {
  const pounds = EPISODIC_LAYERS.flatMap((label) => {
    const item = seriesItems.get(label);
    return item && item.value !== "(0 lbs)" ? [item.value.replace(/^\(|\)$/g, "")] : [];
  });
  if (pounds.length === 0) {
    return null;
  }
  const value =
    new Set(pounds).size === 1
      ? `(${pounds[0]})`
      : `(vendor ${pounds[0]} · e‑Manifest ${pounds[1]})`;
  return { color: colorMap["Episodic (excluded)"], capitalizedLabel: "Episodic (excluded)", value };
}

export function AxisTooltip({
  monthStatusData,
  includeStateRegulatedWaste,
}: {
  monthStatusData: Array<{
    /** First-of-month date ("YYYY-MM-DD") this bar covers. */
    month: string;
    reportedGeneratorStatus: string | null | undefined;
    isOverReportedThreshold: boolean | null | undefined;
  }>;
  includeStateRegulatedWaste: boolean;
}) {
  const tooltipData = useAxesTooltip();
  const dataIndex = tooltipData?.[0]?.dataIndex;

  // Get the hovered month data based on dataIndex
  const hoveredMonthData = useMemo(() => {
    if (dataIndex === undefined) return null;
    return monthStatusData[dataIndex] || null;
  }, [monthStatusData, dataIndex]);

  const reportedFederalGeneratorStatus = hoveredMonthData?.reportedGeneratorStatus || "";
  const isOverReportedThreshold = hoveredMonthData?.isOverReportedThreshold || false;

  // Read the title off the hovered bar's own date. The x-axis labels the year
  // only on January and the first bar, so the axis label alone can't be trusted
  // for it — and a chart-wide year is wrong as soon as the range crosses a year.
  const monthYear = useMemo(
    () => (hoveredMonthData ? prettyPrintMonthYear(hoveredMonthData.month) : ""),
    [hoveredMonthData]
  );

  // Memoize the generator status indicator separately
  const generatorStatusIndicator = useMemo(() => {
    if (!reportedFederalGeneratorStatus) {
      return (
        <Typography variant="body2" color="textSecondary">
          None
        </Typography>
      );
    }

    return (
      <FederalGeneratorStatusIndicator
        status={reportedFederalGeneratorStatus as RcraInfoFederalWasteGeneratorStatus}
        type="chip"
        active={true}
      />
    );
  }, [reportedFederalGeneratorStatus]);

  // Memoize the over threshold indicator separately
  const overThresholdIndicator = useMemo(() => {
    if (!isOverReportedThreshold) return null;

    return (
      <Stack
        direction="row"
        gap={1}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          alignSelf: "stretch",
          backgroundColor: orange[50],
          borderRadius: 0.5,
          padding: 0.5,
        }}
      >
        <OverThresholdDot fontSize={20} />
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{
            flex: 1,
            overflowWrap: "break-word",
            fontStyle: "italic",
          }}
        >
          Above reported status
        </Typography>
      </Stack>
    );
  }, [isOverReportedThreshold]);

  const tooltipTitle = useMemo(() => {
    if (!tooltipData) {
      return null;
    }

    return (
      <Stack spacing={1} mb={1} sx={{ width: 300 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          alignSelf="stretch"
        >
          <Typography variant="h6" fontWeight="800">
            {monthYear}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="textSecondary">
              Federal Reported
            </Typography>
            {generatorStatusIndicator}
          </Stack>
        </Stack>
        {overThresholdIndicator}
      </Stack>
    );
  }, [tooltipData, monthYear, generatorStatusIndicator, overThresholdIndicator]);

  // Memoize the expected fields configuration to avoid recreating it on every render
  const expectedFields = useMemo(() => {
    const fields: Array<{ label: string; color: string }> = [
      { label: "Vendor Hazardous", color: colorMap["Vendor Hazardous"] },
      { label: "Vendor Acute", color: colorMap["Vendor Acute"] },
      {
        label: "e‑Manifest Hazardous",
        color: colorMap["e‑Manifest Hazardous"],
      },
      { label: "e‑Manifest Acute", color: colorMap["e‑Manifest Acute"] },
    ];

    if (includeStateRegulatedWaste) {
      fields.splice(2, 0, {
        label: "Vendor State Regulated",
        color: colorMap["Vendor State Regulated"],
      });
      fields.push({
        label: "e‑Manifest State Regulated",
        color: colorMap["e‑Manifest State Regulated"],
      });
    }
    return fields;
  }, [includeStateRegulatedWaste]);

  // Memoize the series items map creation to avoid recreating it on every render
  const seriesItemsMap = useMemo(() => {
    if (!tooltipData?.[0]?.seriesItems?.length) {
      return new Map<string, SeriesValue>();
    }

    const map = new Map<string, SeriesValue>();
    tooltipData[0].seriesItems.forEach((item) => {
      const splitResult = item.formattedValue.split(":");
      const itemLabel = splitResult[0];
      const amount = splitResult[1];

      // Pre-format the value to avoid repeated string operations
      let formattedValue = amount?.trim() || "(0 lbs)";
      if (formattedValue && !formattedValue.startsWith("(")) {
        formattedValue = `(${formattedValue})`;
      }

      map.set(itemLabel, {
        color: item.color,
        value: formattedValue,
      });
    });

    return map;
  }, [tooltipData]);

  const tooltipLabels = useMemo(() => {
    if (!tooltipData) {
      return [];
    }

    // Build labels array in the order defined by expectedFields
    const labels = expectedFields
      .map((field) => {
        const seriesItem = seriesItemsMap.get(field.label);

        return {
          color: seriesItem?.color || field.color,
          capitalizedLabel: field.label,
          value: seriesItem?.value || "(0 lbs)",
        };
      })
      .filter((label) => label.value !== "(0 lbs)");

    const episodic = episodicRow(seriesItemsMap);
    if (episodic) {
      labels.push(episodic);
    }

    return labels.map((label) => ({
      color: label.color,
      value: (
        <Stack direction="row" alignItems="center" key={label.capitalizedLabel}>
          <Typography variant="body2">{label.capitalizedLabel}</Typography>
          <Typography variant="body2">&nbsp;{label.value}</Typography>
        </Stack>
      ),
    }));
  }, [tooltipData, expectedFields, seriesItemsMap]);

  if (!tooltipData) {
    return null;
  }

  return <ChartTooltip title={tooltipTitle} labels={tooltipLabels} />;
}
